/**
 * Frontend API Types for Mietwerkstatt Backend
 * Vollständige TypeScript-Typen für alle API-Endpunkte
 *
 * Version: 1.0.0
 * Generated: August 2025
 */

// ============================================
// BASE TYPES & ENUMS
// ============================================

export enum UserRole {
  ADMIN = "ADMIN",
  MANAGER = "MANAGER",
  CASHIER = "CASHIER",
  VIEWER = "VIEWER",
}

export enum RentalStatus {
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export enum InvoiceStatus {
  DRAFT = "DRAFT",
  SENT = "SENT",
  PAID = "PAID",
  OVERDUE = "OVERDUE",
  CANCELLED = "CANCELLED",
}

export enum ObjectType {
  HEBEBUEHNE = "HEBEBUEHNE",
  ARBEITSPLATZ = "ARBEITSPLATZ",
  LACKIERKABINE = "LACKIERKABINE",
  PARKPLATZ = "Parkplatz",
}

export enum RentalUnit {
  MINUTES = "MINUTES",
  HOURS = "HOURS",
  DAYS = "DAYS",
}

export enum ArticleCategory {
  TOOL = "TOOL",
  LUBRICANT = "LUBRICANT",
  CONSUMABLE = "CONSUMABLE",
  SERVICE = "SERVICE",
  BUNDLE = "BUNDLE",
}

export interface ArticleCategoryInfo {
  key: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;
}

export interface ArticleUnitInfo {
  key: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;
}

export enum DayOfWeek {
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
}

export enum HolidayType {
  VACATION = "VACATION",
  HOLIDAY = "HOLIDAY",
  MAINTENANCE = "MAINTENANCE",
  OTHER = "OTHER",
}

export enum BusinessStatusMode {
  OPEN = "OPEN",
  REQUEST = "REQUEST",
  CLOSED = "CLOSED",
}

// ============================================
// API RESPONSE WRAPPER
// ============================================

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  details?: string;
  count?: number;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// ============================================
// AUTHENTICATION TYPES
// ============================================

export interface LoginRequest {
  username: string;
  email?: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  tokens: AuthTokens;
}

export interface RefreshTokenResponse {
  tokens: AuthTokens;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateProfileRequest {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  role: UserRole;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

// ============================================
// ENTITY TYPES
// ============================================

export interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isActive: boolean;
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  company?: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  dateOfBirth?: Date;
  drivingLicense?: string;
  licenseExpiryDate?: Date;
  vehicleLicensePlates?: string[];
  isActive: boolean;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Company {
  id: string;
  name: string;
  legalForm?: string;
  street: string;
  zipCode: string;
  city: string;
  country: string;
  phone?: string;
  email?: string;
  website?: string;
  taxNumber?: string;
  vatNumber?: string;
  isSmallBusiness: boolean;
  defaultVatRate?: number;
  smallBusinessNote?: string;
  bankName?: string;
  iban?: string;
  bic?: string;
  invoiceFooter?: string;
  logoPath?: string;
  reservationsEnabled: boolean;
  reservableHebebuehnePercent: number;
  reservableArbeitsplatzPercent: number;
  isActive: boolean;
  isPrimary: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface RentalObject {
  id: string;
  type: ObjectType;
  name: string;
  description?: string;
  location?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  year?: number;
  weight?: number;
  maxCapacity?: number;
  dimensions?: number;
  specifications?: Record<string, any>;
  isActive: boolean;
  isPublic: boolean;
  requiresTraining: boolean;
  notes?: string;
  qrCode?: string;
  defaultArticleIds?: string[];
  additionalArticleIds?: string[];
  // Neue Mieteinheit-Felder
  rentalUnit: RentalUnit;
  minimumRentalDuration: number;
  rentalInterval: number;
  basePrice: number;
  createdAt: Date;
  updatedAt: Date;
  // Extended data (available when includeRelations=true)
  priceStructures?: PriceStructure[];
  defaultArticles?: Article[];
  additionalArticles?: Article[];
  bundleArticles?: RentalObjectBundle[]; // Bundle-Artikel
  // Live rental data
  currentRental?: Rental;
  isAvailable?: boolean;
  currentPrice?: number;
  currentDuration?: string;
  priceBreakdown?: string;
  bundlePrice?: number; // Bundle-Gesamtpreis
  internalBlocker?: {
    active: boolean;
    reason?: string;
    source?: string;
    createdAt?: string;
    escalationId?: string;
  };
}

export interface AiSettings {
  liftOccupancyCheckEnabled: boolean;
}

export type PresenceCameraRole = "gate" | "presence" | "both";

export type PresenceObjectType = "lift" | "workplace" | "gate" | "presence";

export interface PresenceObject {
  id: string;
  type: PresenceObjectType;
  label: string;
}

export interface PresenceZone {
  objectId: string;
  polygon: Array<[number, number]>;
}

export interface PresenceCameraEntry {
  id: string;
  label: string;
  role: PresenceCameraRole;
  gateZone?: Array<[number, number]>;
  liftZones?: Array<{
    lift: number;
    polygon: Array<[number, number]>;
  }>;
  zones?: PresenceZone[];
  indoorReference?: Array<[number, number]>;
}

export interface PresenceCameraConfig {
  enabled: boolean;
  baseUrl: string;
  key: string;
  cid: string;
  warm: boolean;
  cameras: PresenceCameraEntry[];
  objects?: PresenceObject[];
}

export interface PresenceObjectVerdict {
  id: string;
  type: PresenceObjectType;
  label: string;
  status: "occupied" | "free" | "present" | "open" | "closed";
  occupied?: boolean;
  present?: boolean;
  open?: boolean;
  uncertain?: boolean;
  confidence?: number;
  cameras?: string[];
}

export interface PresenceObjectCheckResult {
  ok: boolean;
  skipped?: boolean;
  error?: string;
  objects?: PresenceObjectVerdict[];
  liftTotal?: number;
  liftFree?: number;
  occupiedCount?: number;
  cameras?: Array<{
    cameraId: string;
    label?: string;
    ok: boolean;
    error?: string;
    width?: number;
    height?: number;
    predictions?: Array<{
      label: string;
      confidence: number;
      x_min: number;
      y_min: number;
      x_max: number;
      y_max: number;
    }>;
  }>;
}

export interface PresenceCameraResultEntry {
  id: string;
  label: string;
  role: PresenceCameraRole;
  ok: boolean;
  tor_offen?: boolean;
  person_sichtbar?: boolean;
  error?: string;
}

export interface PresenceCameraVerdict {
  ok: boolean;
  skipped?: boolean;
  doorOpen?: boolean;
  personPresent?: boolean;
  someoneHere?: boolean;
  beschreibung?: string;
  error?: string;
  cameras?: PresenceCameraResultEntry[];
}

export interface LiftOccupancyDryRunLift {
  lift: number;
  configuredZones: number;
  checkedCameras: string[];
  status: "suspected_occupied" | "looks_free" | "not_checked" | "occupied";
  wouldEscalate: boolean;
  chatStatus: "pending_human" | "answered" | "skipped";
  message: string;
  confidence?: number;
}

export interface LiftOccupancyDryRunResult {
  checkedAt: string;
  enabled: boolean;
  cameraConfigured: boolean;
  wouldRunInChat: boolean;
  availability: {
    total: number;
    free: number;
    occupied: number;
    internalBlocked: number;
    recentlyEnded: number;
  } | null;
  summary: string;
  wouldEscalate: boolean;
  appliedInternalBlocks?: number;
  lifts: LiftOccupancyDryRunLift[];
  error?: string;
}

export interface PriceStructure {
  id: string;
  name: string;
  description?: string;
  objectType?: ObjectType;
  isGlobalForType: boolean;
  hourlyRate: number;
  priceBreaks?: Array<{
    fromHours: number;
    toHours?: number;
    rate: number;
    type: "HOURLY";
  }>;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Article {
  id: string;
  name: string;
  description?: string;
  category: string;
  price: number;
  unit: string;
  sku?: string;
  isActive: boolean;
  isPublic: boolean;
  basePrice: number;
  currency: string;
  imagePath?: string;
  stockQuantity?: number;
  taxRate?: number;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface RentalObjectBundle {
  id: string;
  rentalObjectId: string;
  articleId: string;
  quantity: number;
  bundlePrice: number; // Bundle-Gesamtpreis pro Mieteinheit (ersetzt den Grundpreis!)
  isActive: boolean;
  isPublic: boolean;
  notes?: string;
  article?: Article; // Populated article data
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Rental {
  id: string;
  customerId?: string;
  objectId: string;
  status: RentalStatus;
  startDate: Date;
  endDate?: Date;
  plannedEndDate?: Date;
  unitPrice: number; // Preis pro Einheit (ersetzt hourlyRate)
  rentalUnit: string; // Die Einheit für unitPrice (MINUTES, HOURS, DAYS)
  totalAmount?: number;
  paidAmount?: number;
  depositAmount?: number;
  depositPaid: boolean;
  notes?: string;
  bundleId?: string; // Bundle ID wenn Bundle-Vermietung
  damageReport?: Array<{
    description: string;
    photos: string[];
    reportedAt: Date;
    reportedBy: string;
  }>;
  createdBy: string;
  modifiedBy?: string;
  createdAt: Date;
  updatedAt: Date;
  // Extended data
  customer?: Customer;
  object?: RentalObject;
  creator?: User;
  modifier?: User;
  bundle?: RentalObjectBundle; // Bundle-Objekt wenn bundleId vorhanden
  items?: RentalItem[];
  durationHours?: number;
  itemsTotal?: number;
  grandTotal?: number;
}

export interface RentalItem {
  id: string;
  rentalId: string;
  articleId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  unit: string;
  description?: string;
  startedAt?: Date;
  endedAt?: Date;
  billedQuantity?: number;
  discountPercent?: number;
  discountAmount?: number;
  createdAt: Date;
  updatedAt: Date;
  article?: Article;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  companyId: string;
  rentalId?: string;
  status: InvoiceStatus;
  issueDate: Date;
  dueDate?: Date;
  subtotal: number;
  taxRate?: number;
  taxAmount?: number;
  isSmallBusiness: boolean;
  totalAmount: number;
  paidAmount?: number;
  items?: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    unit?: string; // Einheit des Artikels
    category?: string; // Kategorie des Artikels (für Anzeige-Logik)
    articleId?: string; // Referenz zum Original-Artikel
  }>;
  notes?: string;
  pdfPath?: string;
  qrCode?: string;
  createdAt: Date;
  updatedAt: Date;
  customer?: Customer;
  company?: Company;
  rental?: Rental;
}

export interface OpeningHours {
  id: string;
  companyId: string;
  dayOfWeek: DayOfWeek;
  openTime?: string;
  closeTime?: string;
  isClosed: boolean;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Holiday {
  id: string;
  companyId: string;
  name: string;
  startDate: Date | string;
  endDate: Date | string;
  type: HolidayType;
  description?: string;
  isActive: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface BusinessStatus {
  id: string;
  companyId: string;
  isOpen: boolean;
  overrideStatus?: BusinessStatusMode;
  overrideUntil?: Date;
  overrideReason?: string;
  manualCloseReason?: string;
  manualCloseUntil?: Date;
  statusMessage?: string;
  lastChangedBy?: string;
  lastUpdated: Date;
}

export interface ApiKey {
  id: string;
  keyValue: string;
  name: string;
  description?: string;
  permissions: string[];
  isActive: boolean;
  expiresAt?: Date;
  lastUsed?: Date;
  usageCount: number;
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// REQUEST TYPES
// ============================================

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  role: UserRole;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export interface UpdateUserRequest {
  username?: string;
  email?: string;
  role?: UserRole;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface ResetUserPasswordRequest {
  newPassword: string;
}

export interface CreateCustomerRequest {
  firstName: string;
  lastName: string;
  company?: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  dateOfBirth?: Date;
  drivingLicense?: string;
  licenseExpiryDate?: Date;
  vehicleLicensePlates?: string[];
  notes?: string;
}

export interface UpdateCustomerRequest {
  firstName?: string;
  lastName?: string;
  company?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  dateOfBirth?: Date;
  drivingLicense?: string;
  licenseExpiryDate?: Date;
  vehicleLicensePlates?: string[];
  isActive?: boolean;
  notes?: string;
}

export interface CreateCompanyRequest {
  name: string;
  legalForm?: string;
  street: string;
  zipCode: string;
  city: string;
  country: string;
  phone?: string;
  email?: string;
  website?: string;
  taxNumber?: string;
  vatNumber?: string;
  isSmallBusiness?: boolean;
  defaultVatRate?: number;
  smallBusinessNote?: string;
  bankName?: string;
  iban?: string;
  bic?: string;
  invoiceFooter?: string;
  logoPath?: string;
  reservationsEnabled?: boolean;
  reservableHebebuehnePercent?: number;
  reservableArbeitsplatzPercent?: number;
  isPrimary?: boolean;
  isActive?: boolean;
}

export interface UpdateCompanyRequest {
  name?: string;
  legalForm?: string;
  street?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  taxNumber?: string;
  vatNumber?: string;
  isSmallBusiness?: boolean;
  defaultVatRate?: number;
  smallBusinessNote?: string;
  bankName?: string;
  iban?: string;
  bic?: string;
  invoiceFooter?: string;
  logoPath?: string;
  reservationsEnabled?: boolean;
  reservableHebebuehnePercent?: number;
  reservableArbeitsplatzPercent?: number;
  isActive?: boolean;
}

export interface ChatReservation {
  id: string;
  customerName: string;
  customerPhone: string;
  objectType: ObjectType.HEBEBUEHNE | ObjectType.ARBEITSPLATZ;
  reservationDate: string;
  startTime: string;
  estimatedHours: number;
  workDescription: string;
  status: "RESERVED" | "CANCELLED";
  source?: string;
  sessionId?: string;
  notes?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface ChatVisit {
  id: string;
  customerName: string;
  customerPhone?: string;
  visitDate: string;
  startTime: string;
  estimatedHours?: number;
  workDescription: string;
  status: "PLANNED" | "CANCELLED";
  source?: string;
  sessionId?: string;
  escalationId?: string;
  notes?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface CreateChatVisitRequest {
  customerName: string;
  customerPhone?: string;
  visitDate: string;
  startTime: string;
  estimatedHours?: number;
  workDescription: string;
  notes?: string;
}

export interface ReservationPolicy {
  reservationsEnabled: boolean;
  reservableHebebuehnePercent: number;
  reservableArbeitsplatzPercent: number;
  capacity?: {
    HEBEBUEHNE: { total: number; reservable: number; percent: number };
    ARBEITSPLATZ: { total: number; reservable: number; percent: number };
  };
}

export interface CreateChatReservationRequest {
  customerName: string;
  customerPhone: string;
  objectType: ObjectType.HEBEBUEHNE | ObjectType.ARBEITSPLATZ;
  reservationDate: string;
  startTime: string;
  estimatedHours: number;
  workDescription: string;
  notes?: string;
}

export interface CreateRentalObjectRequest {
  type: ObjectType;
  name: string;
  description?: string;
  location?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  year?: number;
  weight?: number;
  maxCapacity?: number;
  dimensions?: number;
  specifications?: Record<string, any>;
  requiresTraining?: boolean;
  notes?: string;
  qrCode?: string;
  defaultArticleIds?: string[];
  additionalArticleIds?: string[];
  // Neue Mieteinheit-Felder
  rentalUnit: RentalUnit;
  minimumRentalDuration: number;
  rentalInterval: number;
  basePrice: number;
}

export interface UpdateRentalObjectRequest {
  type?: ObjectType;
  name?: string;
  description?: string;
  location?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  year?: number;
  weight?: number;
  maxCapacity?: number;
  dimensions?: number;
  specifications?: Record<string, any>;
  isActive?: boolean;
  isPublic?: boolean;
  requiresTraining?: boolean;
  notes?: string;
  qrCode?: string;
  defaultArticleIds?: string[];
  additionalArticleIds?: string[];
  // Neue Mieteinheit-Felder
  rentalUnit?: RentalUnit;
  minimumRentalDuration?: number;
  rentalInterval?: number;
  basePrice?: number;
}

export interface CreatePriceStructureRequest {
  name: string;
  description?: string;
  objectType?: ObjectType;
  isGlobalForType?: boolean;
  hourlyRate: number;
  priceBreaks?: Array<{
    fromHours: number;
    toHours?: number;
    rate: number;
    type: "HOURLY";
  }>;
}

export interface UpdatePriceStructureRequest {
  name?: string;
  description?: string;
  objectType?: ObjectType;
  isGlobalForType?: boolean;
  hourlyRate?: number;
  priceBreaks?: Array<{
    fromHours: number;
    toHours?: number;
    rate: number;
    type: "HOURLY";
  }>;
  isActive?: boolean;
}

export interface CreateArticleRequest {
  name: string;
  description?: string;
  category: string;
  price: number;
  unit: string;
  sku?: string;
  notes?: string;
  isPublic?: boolean;
  isActive?: boolean;
}

export interface UpdateArticleRequest {
  name?: string;
  description?: string;
  category?: string;
  price?: number;
  unit?: string;
  sku?: string;
  notes?: string;
  isPublic?: boolean;
  isActive?: boolean;
}

export interface CreateRentalRequest {
  customerId?: string;
  objectId: string;
  startDate: Date;
  plannedEndDate?: Date;
  hourlyRate: number;
  depositAmount?: number;
  notes?: string;
  items?: CreateRentalItemRequest[];
}

export interface CreateRentalItemRequest {
  articleId: string;
  quantity: number;
  unitPrice?: number;
  description?: string;
  discountPercent?: number;
  startedAt?: Date;
  endedAt?: Date;
}

export interface UpdateRentalRequest {
  startDate?: Date;
  endDate?: Date;
  plannedEndDate?: Date;
  hourlyRate?: number;
  depositAmount?: number;
  depositPaid?: boolean;
  notes?: string;
  status?: RentalStatus;
}

export interface CreateInvoiceRequest {
  customerId?: string;
  subtotal: number;
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    unit?: string; // Einheit des Artikels
    category?: string; // Kategorie des Artikels (für Anzeige-Logik)
    articleId?: string; // Referenz zum Original-Artikel
  }>;
  notes?: string;
  rentalId?: string; // Optional für Mieten
}

export interface UpdateInvoiceStatusRequest {
  status: InvoiceStatus;
  paidAt?: Date;
}

export interface SetBusinessStatusRequest {
  status?: BusinessStatusMode;
  isOpen?: boolean;
  reason?: string;
  until?: Date;
  statusMessage?: string;
}

export interface SetOpeningHoursRequest {
  dayOfWeek: DayOfWeek;
  openTime?: string;
  closeTime?: string;
  isClosed?: boolean;
}

export interface CreateHolidayRequest {
  name: string;
  startDate: Date;
  endDate: Date;
  type: HolidayType;
  description?: string;
}

export interface CreateApiKeyRequest {
  name: string;
  permissions: string[];
  description?: string;
  expiresAt?: Date;
}

export interface AddAdditionalArticlesRequest {
  articleIds: string[];
}

export interface SetAdditionalArticlesRequest {
  articleIds: string[];
}

export interface QuickActionRequest {
  action: "addArticles" | "endRental";
  articleIds?: string[];
}

export interface LivePriceCalculation {
  duration: number; // Tatsächliche Dauer in Minuten
  billedDuration: number; // Abgerechnete Dauer in Minuten
  basePrice: number;
  articlesPrice: number;
  subtotal: number;
  tax: number;
  total: number;
  breakdown: {
    timeUnit: string;
    rate: number;
    quantity: number;
    amount: number;
  };
}

// ============================================
// FILTER & QUERY TYPES
// ============================================

export interface UserFilters {
  role?: UserRole;
  isActive?: boolean;
  searchTerm?: string;
}

export interface CustomerFilters {
  isActive?: boolean;
  searchTerm?: string;
  city?: string;
  hasRentals?: boolean;
}

export interface CompanyFilters {
  isActive?: boolean;
  searchTerm?: string;
  city?: string;
}

export interface RentalObjectFilters {
  type?: ObjectType;
  isActive?: boolean;
  isPublic?: boolean;
  isAvailable?: boolean;
  searchTerm?: string;
  location?: string;
  manufacturer?: string;
  requiresTraining?: boolean;
  includeInactive?: boolean; // Neuer Filter für deaktivierte Objekte
}

export interface PriceStructureFilters {
  isGlobal?: boolean;
  objectType?: ObjectType;
  isActive?: boolean;
  searchTerm?: string;
}

export interface ArticleFilters {
  category?: string;
  isActive?: boolean;
  searchTerm?: string;
}

export interface RentalFilters {
  status?: RentalStatus;
  customerId?: string;
  objectId?: string;
  startDate?: Date;
  endDate?: Date;
}

export interface InvoiceFilters {
  status?: InvoiceStatus;
  customerId?: string;
  startDate?: Date;
  endDate?: Date;
}

// ============================================
// STATISTICS TYPES
// ============================================

export interface UserStats {
  total: number;
  active: number;
  byRole: Array<{ role: string; count: number }>;
}

export interface CustomerStats {
  total: number;
  active: number;
  inactive: number;
  withRentals: number;
  topCities: Array<{ city: string; count: number }>;
}

export interface CompanyStats {
  total: number;
  active: number;
  inactive: number;
  withSmallBusiness: number;
  byCity: Array<{ city: string; count: number }>;
}

export interface RentalObjectStats {
  total: number;
  active: number;
  available: number;
  byType: Array<{ type: string; count: number }>;
  byLocation: Array<{ location: string; count: number }>;
  requiresTraining: number;
}

export interface ArticleStats {
  total: number;
  active: number;
  byCategory: Array<{ category: string; count: number }>;
  averagePrice: number;
}

export interface BusinessStatusInfo {
  isOpen: boolean;
  status?: BusinessStatusMode;
  isManualOverride?: boolean;
  overrideUntil?: Date;
  reason?: string;
  nextOpenTime?: Date;
  statusMessage?: string;
}

// ============================================
// PRICING TYPES
// ============================================

export interface PriceCalculation {
  basePrice: number;
  defaultArticlesPrice: number;
  additionalArticlesPrice: number;
  totalPrice: number;
  priceDetails: {
    base: { name: string; hourlyRate: number; hours: number; total: number };
    defaultArticles: Array<{
      name: string;
      hourlyRate: number;
      hours: number;
      total: number;
    }>;
    additionalArticles: Array<{
      name: string;
      hourlyRate: number;
      hours: number;
      total: number;
    }>;
  };
}

export interface LivePriceUpdate {
  currentDurationMinutes: number;
  baseAmount: number;
  itemsTotal: number;
  currentTotal: number;
  projectedTotal?: number;
  priceBreakdown: string; // JSON stringified breakdown
}

export interface BundleCatalogEntry {
  objectId: string;
  objectName: string;
  objectType: ObjectType;
  basePrice: {
    hourly: number;
    daily: number;
    weekly: number;
    monthly: number;
  };
  defaultArticles: Array<{
    id: string;
    name: string;
    price: number;
  }>;
  additionalArticles: Array<{
    id: string;
    name: string;
    price: number;
  }>;
  bundlePrice: {
    hourly: number;
    daily: number;
    weekly: number;
    monthly: number;
  };
}

// ============================================

// ============================================
// VALIDATION TYPES
// ============================================

export interface ValidationResult {
  isValid: boolean;
  errors?: string[];
}

export interface VatNumberValidation {
  vatNumber: string;
  isValid: boolean;
}

export interface IbanValidation {
  iban: string;
  isValid: boolean;
}

// ============================================
// FILE UPLOAD TYPES
// ============================================

export interface FileUploadResponse {
  success: boolean;
  filePath: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

// ============================================
// PAGINATION TYPES
// ============================================

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

// ============================================
// SEARCH TYPES
// ============================================

export interface SearchParams {
  q: string;
  limit?: number;
}

export interface AutocompleteResult<T> {
  items: T[];
  total: number;
}

// ============================================
// AVAILABILITY TYPES
// ============================================

export interface AvailabilityCheck {
  objectId: string;
  isAvailable: boolean;
  currentRental?: Rental;
}

export interface AvailabilitySchedule {
  objectId: string;
  schedule: Array<{
    date: Date;
    isAvailable: boolean;
    rental?: Rental;
  }>;
}

// ============================================
// REVENUE ANALYTICS TYPES
// ============================================

export interface RevenueFilters {
  startDate?: string;
  endDate?: string;
  status?: InvoiceStatus[];
  customerId?: string;
  period?: "day" | "week" | "month" | "quarter" | "year";
  groupBy?:
    | "day"
    | "week"
    | "month"
    | "quarter"
    | "year"
    | "customer"
    | "status";
}

export interface RevenueStats {
  totalRevenue: number;
  totalInvoices: number;
  averageInvoiceAmount: number;
  paidRevenue: number;
  pendingRevenue: number;
  overdueRevenue: number;
  revenueByStatus: StatusRevenue[];
  revenueByPeriod: PeriodRevenue[];
  topCustomers: CustomerRevenue[];
  monthlyComparison: MonthlyComparison[];
}

export interface StatusRevenue {
  status: InvoiceStatus;
  totalAmount: number;
  count: number;
  percentage: number;
}

export interface PeriodRevenue {
  period: string;
  totalAmount: number;
  count: number;
  averageAmount: number;
  previousAmount?: number;
  previousCount?: number;
  growth?: number;
  growthPercentage?: number;
}

export interface CustomerRevenue {
  customerId: string;
  customerName: string;
  totalAmount: number;
  invoiceCount: number;
  averageAmount: number;
}

export interface MonthlyComparison {
  month: string;
  currentYear: number;
  previousYear: number;
  growth: number;
  growthPercentage: number;
}

export interface RevenueExportData {
  summary: {
    totalRevenue: number;
    totalInvoices: number;
    averageInvoice: number;
    period: string;
  };
  breakdown: Array<{
    date: string;
    amount: number;
    invoiceCount: number;
  }>;
  topCustomers: Array<{
    customer: string;
    amount: number;
    invoices: number;
  }>;
  statusBreakdown: Array<{
    status: string;
    amount: number;
    count: number;
    percentage: string;
  }>;
}

export interface RevenueOverview {
  thisWeek: {
    totalRevenue: number;
    totalInvoices: number;
    averageInvoice: number;
  };
  thisMonth: {
    totalRevenue: number;
    totalInvoices: number;
    averageInvoice: number;
  };
  thisYear: {
    totalRevenue: number;
    totalInvoices: number;
    averageInvoice: number;
  };
}

export interface RevenueTrends {
  current: RevenueStats;
  previous: RevenueStats;
  comparison: {
    revenueGrowth: number;
    revenueGrowthPercentage: number;
    invoiceGrowth: number;
    averageInvoiceChange: number;
  };
}

// ============================================
// 3CX INTEGRATION TYPES
// ============================================

export interface ThreeCXStatus {
  connected: boolean;
  tokenStatus: {
    hasToken: boolean;
    expiresAt: number;
    expiresIn: number;
    isValid: boolean;
  };
  lastSync?: Date;
  errors?: string[];
}

export interface ThreeCXSyncResult {
  openingHours: boolean;
  holidays: boolean;
  errors: string[];
}

export interface ThreeCXOpeningHoursPeriod {
  DayOfWeek: string;
  Start: string;
  Stop: string;
}

export interface ThreeCXHoliday {
  Name: string;
  IsRecurrent: boolean;
  HolidayPrompt: string;
  Day: number;
  Month: number;
  Year: number;
  TimeOfStartDate: string;
  DayEnd: number;
  MonthEnd: number;
  YearEnd: number;
  TimeOfEndDate: string;
  Id?: number;
}

export interface ThreeCXComparison {
  differences: Array<{
    type:
      | "MISSING_IN_3CX"
      | "TIME_DIFFERENCE"
      | "DATE_DIFFERENCE"
      | "EXTRA_IN_3CX";
    day?: string;
    name?: string;
    business: any;
    threeCX: any;
  }>;
  needsUpdate: boolean;
  businessHours?: ThreeCXOpeningHoursPeriod[];
  businessHolidays?: ThreeCXHoliday[];
  threeCXHours?: ThreeCXOpeningHoursPeriod[];
  threeCXHolidays?: ThreeCXHoliday[];
  summary: string;
}

export interface ThreeCXForceUpdateResult {
  success: boolean;
  before: any[];
  after: any[];
  applied: any[];
  summary: string;
}

export interface ThreeCXSyncAnalysis {
  openingHours: {
    differences: any[];
    needsUpdate: boolean;
    summary: string;
  };
  holidays: {
    differences: any[];
    needsUpdate: boolean;
    summary: string;
  };
  overallStatus: "SYNC" | "NEEDS_UPDATE" | "ERROR";
}

// ============================================
// PHONE OFFSET TYPES
// ============================================

export interface PhoneOffsets {
  before: string | null;
  after: string | null;
}

export interface SetPhoneOffsetsRequest {
  beforeOffset: string | null;
  afterOffset: string | null;
}

export interface SetPhoneOffsetsResponse {
  success: boolean;
  message: string;
  data: {
    before: any;
    after: any;
    threeCXSync: {
      success: boolean;
      error?: string;
    };
  };
}

// ============================================
// LEGACY IMPORT TYPES
// ============================================

export interface LegacyInvoiceRow {
  UUID: string;
  Belegnummer: string;
  Benutzer: string;
  ID: string;
  Name: string;
  Menge: string;
  Preis: string;
  MwSt: string;
  Datum: string;
  Vermerk: string;
}

export interface LegacyInvoiceGroup {
  uuid: string;
  invoiceNumber: string;
  date: Date;
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    unit: string;
    category: string;
    articleId?: string;
  }>;
  subtotal: number;
  totalAmount: number;
}

export interface LegacyImportResult {
  success: boolean;
  importedCount: number;
  skippedCount: number;
  errors: string[];
  summary: {
    totalRows: number;
    uniqueInvoices: number;
    totalAmount: number;
    dateRange: {
      from: string;
      to: string;
    };
  };
}

export interface LegacyImportValidation {
  isValid: boolean;
  errors: string[];
  preview: {
    totalRows: number;
    uniqueInvoices: number;
    sampleRows: LegacyInvoiceRow[];
  };
}

export interface LegacyImportFileRequest {
  file: File;
}

export interface LegacyImportTextRequest {
  csvContent: string;
}

// ============================================
// SYSTEM SETTINGS TYPES
// ============================================

export interface SystemInfo {
  version: string;
  nodeVersion: string;
  platform: string;
  architecture: string;
  uptime: string;
  memoryUsage: {
    used: string;
    total: string;
    percentage: number;
  };
  databaseSize: string;
  lastBackup: string | null;
  activeUsers: number;
  totalRentals: number;
  totalRevenue: string;
  activeRentals: number;
  todayRentals: number;
  systemLoad: {
    cpu: number;
    memory: number;
  };
}

export interface SystemActionResult {
  success: boolean;
  message: string;
  timestamp: string;
  details?: any;
  error?: string;
}

export interface BackupResult extends SystemActionResult {
  backupPath?: string;
  backupSize?: string;
}

export interface LogEntry {
  timestamp: string;
  level: "ERROR" | "WARN" | "INFO" | "DEBUG";
  message: string;
  source?: string;
}

export interface SystemHealthCheck {
  status: "ok" | "warning" | "critical" | "info";
  value?: number;
  message: string;
}

export interface SystemHealth {
  overall: "healthy" | "warning" | "critical";
  checks: {
    memory: SystemHealthCheck;
    cpu: SystemHealthCheck;
    database: SystemHealthCheck;
    backup: SystemHealthCheck;
    activeRentals: SystemHealthCheck;
  };
  timestamp: string;
}

export interface SystemStats {
  runtime: {
    uptime: string;
    version: string;
    nodeVersion: string;
    platform: string;
  };
  performance: {
    memory: {
      used: string;
      total: string;
      percentage: number;
    };
    cpu: number;
    database: string;
  };
  business: {
    totalRentals: number;
    activeRentals: number;
    todayRentals: number;
    totalRevenue: string;
    activeUsers: number;
  };
  backup: {
    lastBackup: string | null;
    status: "available" | "none";
  };
}
